'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeaderWithActions } from '@/components/ui/card';
import { Expand, Minimize } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface ExpandableCardProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  breadcrumb?: React.ReactNode;
  pageHeader?: React.ReactNode;
  additionalActions?: React.ReactNode;
  onExpandChange?: (expanded: boolean) => void;
  /**
   * 카드가 남은 세로 공간을 정확히 채운다. 그리드 하나만 있는 화면에서 쓴다.
   * 탭 헤더·필터바 높이를 100vh 에서 빼는 상수 대신 flex 로 남은 높이를 받으므로,
   * 필터바가 상세 필터로 늘어나도 스크롤이 생기지 않는다.
   */
  fill?: boolean;
  className?: string;
  contentClassName?: string;
}

export function ExpandableCard({
  title,
  children,
  breadcrumb,
  pageHeader,
  additionalActions,
  onExpandChange,
  fill = false,
  className = '',
  contentClassName = '',
}: ExpandableCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // 전체화면 확장/축소 핸들러
  const handleExpandToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    // 콜백 함수 호출
    onExpandChange?.(newExpanded);
  };

  // ESC 키로 전체화면 종료
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
        onExpandChange?.(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isExpanded, onExpandChange]);

  return (
    <div
      className={`${isExpanded ? 'fixed inset-0 z-50 bg-white' : 'p-3 space-y-3'} ${fill ? 'flex flex-col min-h-0 h-full' : ''}`}
    >
      {/* Breadcrumb 과 액션 버튼을 한 줄에 둔다. 별도 제목 줄을 없애 그리드에 세로 공간을
          더 준다 - 화면 이름은 Breadcrumb 의 마지막 항목과 카드 헤더가 말한다.
          전체화면일 때는 둘 다 숨긴다. */}
      {!isExpanded && (breadcrumb || pageHeader) && (
        <div className='flex items-center justify-between gap-3'>
          {breadcrumb}
          {pageHeader}
        </div>
      )}

      {/* Main Content */}
      <div
        className={`${isExpanded ? 'h-screen' : 'w-full'} ${fill ? 'flex-1 min-h-0' : ''}`}
      >
        <Card
          ref={cardRef}
          className={`${isExpanded ? 'h-full rounded-none border-0' : ''} ${fill ? 'h-full flex flex-col min-h-0' : ''} ${className}`}
        >
          <CardHeaderWithActions
            title={typeof title === 'string' ? title : ''}
            actions={
              <div className='flex items-center space-x-2'>
                {additionalActions}
                <Button
                  size='sm'
                  onClick={handleExpandToggle}
                  title={isExpanded ? '축소' : '펼치기'}
                  className='h-6 w-6 p-0 bg-none border-2 border-gray-300 hover:bg-red-50'
                >
                  {isExpanded ? (
                    <Minimize className='h-4 w-4' />
                  ) : (
                    <Expand className='h-4 w-4' />
                  )}
                </Button>
              </div>
            }
          />

          <CardContent
            className={`space-y-4 ${fill ? 'flex-1 min-h-0 flex flex-col' : isExpanded ? 'h-[calc(100vh-60px)] overflow-auto' : ''} ${contentClassName}`}
          >
            {children}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
